export type BillingLockLevel = 'none' | 'invoiced' | 'financial_locked';

export interface OrderBillingContext {
  hasInvoice: boolean;
  lockLevel: BillingLockLevel;
  invoiceId: number | null;
  invoiceNumber: string | null;
  invoiceStatus: string | null;
  paidAmount: number;
  appliedCredit: number;
  canEditOrderLines: boolean;
  canEditOrderFinancials: boolean;
  canEditInvoiceLines: boolean;
  orderEditHint: string;
  invoiceEditHint: string;
}

const FINANCIAL_LOCKED_STATUSES = new Set([
  'Paid', 'Partial', 'Partially Paid', 'Sent', 'Overdue', 'Cancelled',
]);

export function evaluateBillingContext(invoice: any | null | undefined): OrderBillingContext {
  if (!invoice) {
    return {
      hasInvoice: false,
      lockLevel: 'none',
      invoiceId: null,
      invoiceNumber: null,
      invoiceStatus: null,
      paidAmount: 0,
      appliedCredit: 0,
      canEditOrderLines: true,
      canEditOrderFinancials: true,
      canEditInvoiceLines: false,
      orderEditHint: 'Edit this order freely. When ready to bill, generate an invoice.',
      invoiceEditHint: '',
    };
  }

  const paidAmount = Number(invoice.paid_amount ?? invoice.paidAmount ?? 0);
  const appliedCredit = Number(invoice.applied_credit ?? invoice.appliedCredit ?? 0);
  const status = String(invoice.status || 'Draft');
  const invoiceNumber = invoice.invoice_number || invoice.invoiceNumber || `#${invoice.id}`;
  const hasMoneyApplied = paidAmount > 0.009 || appliedCredit > 0.009;
  const statusLocked = FINANCIAL_LOCKED_STATUSES.has(status);
  const financialLocked = hasMoneyApplied || statusLocked;
  const lockLevel: BillingLockLevel = financialLocked ? 'financial_locked' : 'invoiced';

  return {
    hasInvoice: true,
    lockLevel,
    invoiceId: Number(invoice.id),
    invoiceNumber: String(invoiceNumber),
    invoiceStatus: status,
    paidAmount,
    appliedCredit,
    canEditOrderLines: false,
    canEditOrderFinancials: false,
    canEditInvoiceLines: !financialLocked && status === 'Draft',
    orderEditHint: financialLocked
      ? `Invoiced as ${invoiceNumber}. Lines and prices are locked (payment or credit recorded). Update delivery/status here — use a Credit Note to change amounts.`
      : `Invoiced as ${invoiceNumber}. To change products or prices, edit the invoice — not this order.`,
    invoiceEditHint: financialLocked
      ? 'This invoice has payment or credit applied. Issue a Credit Note to adjust amounts.'
      : 'Draft invoice — edit lines and totals here.',
  };
}

export function billingFromOrderResponse(order: any): OrderBillingContext {
  if (order?.billingContext) return order.billingContext;
  if (order?.linkedInvoice) return evaluateBillingContext(order.linkedInvoice);
  return evaluateBillingContext(null);
}
